import { CartAddEvent } from '@theme/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const cache = new Map();
const slideIndex = new Map();

function formatMoney(cents, sample) {
  sample = sample || '€1,00';
  const m = sample.match(/1([.,])00/);
  if (!m) return sample.replace('1', String(Math.round(cents / 100)));
  const dec = m[1];
  const thou = dec === ',' ? '.' : ',';
  const [i, d] = (cents / 100).toFixed(2).split('.');
  return sample.replace(/1[.,]00/, i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + dec + d);
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function imgUrl(src, width) {
  if (!src) return '';
  if (src.startsWith('//')) src = 'https:' + src;
  try {
    const u = new URL(src);
    u.searchParams.set('width', width);
    return u.toString();
  } catch (e) {
    return src;
  }
}

function optionNames(p) {
  return (p.options || []).map((o) => (typeof o === 'string' ? o : o.name));
}

function variantOptions(v) {
  return v.options || [v.option1, v.option2, v.option3].filter((x) => x != null);
}

class LmCartUpsell extends HTMLElement {
  connectedCallback() {
    this._observer = new MutationObserver(() => {
      if (!this.querySelector('.lm-upsell__track')) this.render();
    });
    this._observer.observe(this, { childList: true, attributes: true, attributeFilter: ['data-in-cart', 'data-product-id'] });
    this.render();
  }

  disconnectedCallback() {
    this._observer && this._observer.disconnect();
  }

  async load(pid) {
    if (!cache.has(pid)) {
      cache.set(
        pid,
        fetch(`${ROOT}recommendations/products.json?product_id=${pid}&limit=10&intent=related`)
          .then((r) => r.json())
          .then((d) => d.products || [])
          .catch(() => [])
      );
    }
    return cache.get(pid);
  }

  async render() {
    if (this._rendering) return;
    this._rendering = true;
    try {
      const pid = this.dataset.productId;
      if (!pid) return;
      const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
      const all = await this.load(pid);
      this.products = all.filter((p) => p.available && !inCart.has(String(p.id))).slice(0, 8);
      if (!this.products.length) {
        this.classList.add('lm-upsell--empty');
        this.innerHTML = '';
        return;
      }
      this.classList.remove('lm-upsell--empty');
      this.variants = this.products.map((p) => p.variants.find((v) => v.available) || p.variants[0]);
      this.innerHTML = `<div class="lm-upsell__track">${this.products.map((p, i) => this.card(p, i)).join('')}</div>`;
      this.bind();
    } finally {
      this._rendering = false;
    }
  }

  card(p, i) {
    const variant = this.variants[i];
    const names = optionNames(p);
    const selected = variantOptions(variant);
    const hasOptions = !(names.length === 1 && p.variants.length === 1);
    const selects = hasOptions
      ? names
          .map((name, oi) => {
            const values = [...new Set(p.variants.map((v) => variantOptions(v)[oi]))];
            return `<select class="lm-upsell__select" aria-label="${esc(name)}">${values
              .map((val) => `<option value="${esc(val)}"${val === selected[oi] ? ' selected' : ''}>${esc(val)}</option>`)
              .join('')}</select>`;
          })
          .join('')
      : '';
    const img = p.featured_image || (p.images && p.images[0]) || '';
    return `
      <div class="lm-upsell__card" data-i="${i}">
        <a class="lm-upsell__media" href="${esc(p.url)}">${img ? `<img src="${esc(imgUrl(img, 160))}" alt="${esc(p.title)}" loading="lazy" width="62" height="78">` : ''}</a>
        <div class="lm-upsell__info">
          <a class="lm-upsell__title" href="${esc(p.url)}">${esc(p.title)}</a>
          <div class="lm-upsell__row">
            <div class="lm-upsell__price" data-price>${formatMoney(variant.price, this.dataset.moneySample)}</div>
            ${hasOptions ? `<div class="lm-upsell__selects">${selects}</div>` : ''}
          </div>
          <button type="button" class="lm-upsell__add"${variant.available ? '' : ' disabled'}>${esc(this.dataset.addLabel || 'Add to cart')}</button>
        </div>
      </div>`;
  }

  bind() {
    this.querySelectorAll('.lm-upsell__card').forEach((card) => {
      const i = Number(card.dataset.i);
      card.querySelectorAll('.lm-upsell__select').forEach((s) => s.addEventListener('change', () => this.onOptionChange(card, i)));
      card.querySelector('.lm-upsell__add').addEventListener('click', (e) => this.add(e.currentTarget, i));
    });
  }

  onOptionChange(card, i) {
    const p = this.products[i];
    const chosen = [...card.querySelectorAll('.lm-upsell__select')].map((s) => s.value);
    const v = p.variants.find((x) => variantOptions(x).every((o, k) => o === chosen[k]));
    const btn = card.querySelector('.lm-upsell__add');
    if (!v) {
      btn.disabled = true;
      return;
    }
    this.variants[i] = v;
    card.querySelector('[data-price]').innerHTML = formatMoney(v.price, this.dataset.moneySample);
    btn.disabled = !v.available;
  }

  async add(btn, i) {
    const variant = this.variants[i];
    if (!variant || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
    try {
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      const cart = await fetch(`${ROOT}cart.js`).then((r) => r.json());
      document.dispatchEvent(
        new CartAddEvent(cart, 'lm-upsell', {
          source: 'lm-upsell',
          itemCount: cart.item_count,
          variantId: String(variant.id),
          sections: data.sections,
        })
      );
    } catch (err) {
      console.error('[lm-upsell]', err);
      btn.disabled = false;
    } finally {
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('lm-cart-upsell')) customElements.define('lm-cart-upsell', LmCartUpsell);

function parseMoneyText(txt) {
  const m = txt.match(/\d[\d.,\s]*/);
  if (!m) return null;
  const raw = m[0].replace(/\s/g, '');
  const lastSep = Math.max(raw.lastIndexOf(','), raw.lastIndexOf('.'));
  const hasDec = lastSep > -1 && raw.length - lastSep - 1 === 2;
  const intPart = hasDec ? raw.slice(0, lastSep) : raw;
  const dec = hasDec ? raw.slice(lastSep + 1) : '00';
  return { cents: parseInt(intPart.replace(/[.,]/g, ''), 10) * 100 + parseInt(dec, 10), match: m[0].trim(), decSep: hasDec ? raw[lastSep] : ',' };
}

function fmtLike(sampleText, cents) {
  const parsed = parseMoneyText(sampleText);
  if (!parsed) return '';
  const thou = parsed.decSep === ',' ? '.' : ',';
  const [i, d] = (cents / 100).toFixed(2).split('.');
  return sampleText.replace(parsed.match, i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + parsed.decSep + d);
}

function setStrike(container, cls, text, prepend) {
  let el = container.querySelector('.' + cls);
  if (!text) { el && el.remove(); return; }
  if (el) { if (el.textContent !== text) el.textContent = text; return; }
  el = document.createElement('s');
  el.className = cls;
  el.textContent = text;
  prepend ? container.prepend(el) : container.appendChild(el);
}

/* Devuelve el precio de comparación de la línea (céntimos) y lo pinta encima del precio */
function addLineCompare(row) {
  const price = row.querySelector('.cart-items__price');
  if (!price) return 0;
  const cmp = row.querySelector('.cart-items__unit-price-wrapper .compare-at-price');
  const input = row.querySelector('.cart-items__quantity input');
  const lineText = (price.querySelector('text-component') || price).textContent.trim();
  const line = parseMoneyText(lineText);
  if (!cmp) { setStrike(price, 'lm-line-compare', '', true); return line ? line.cents : 0; }
  const qty = Math.max(1, parseInt(input && input.value, 10) || 1);
  const unit = parseMoneyText(cmp.textContent.trim());
  if (!unit) return line ? line.cents : 0;
  const total = unit.cents * qty;
  setStrike(price, 'lm-line-compare', fmtLike(cmp.textContent.trim(), total), true);
  return total;
}

function decorateCartLines() {
  let compareSum = 0;
  document.querySelectorAll('.cart-drawer__items .cart-items__table-row').forEach((row) => {
    compareSum += addLineCompare(row);
  });

  // "Size: 21x30 cm" / "Frame: No Frame"
  document.querySelectorAll('.cart-drawer__items .lm-optnames').forEach((n) => n.remove());
  document.querySelectorAll('.cart-drawer__items .cart-items__variant dd').forEach((dd) => {
    const t = dd.textContent.replace(/ /g, ' ').trim().replace(/,$/, '').trim();
    if (dd.textContent !== t) dd.textContent = t;
  });

  // Suma de precios de comparación junto al total
  const totalRow = document.querySelector('.cart-drawer__summary .cart-totals__total');
  const totalVal = totalRow && totalRow.querySelector('.cart-totals__total-value');
  if (totalRow && totalVal) {
    const totalText = totalVal.textContent.trim();
    const totalParsed = parseMoneyText(totalText);
    const show = totalParsed && compareSum > totalParsed.cents;
    let el = totalRow.querySelector('.lm-total-compare');
    if (!show) { el && el.remove(); }
    else {
      const txt = fmtLike(totalText, compareSum);
      if (!el) { el = document.createElement('s'); el.className = 'lm-total-compare'; totalVal.before(el); }
      if (el.textContent !== txt) el.textContent = txt;
    }
  }
}
let lmRaf = 0;
const lmScheduleDecorate = () => {
  cancelAnimationFrame(lmRaf);
  lmRaf = requestAnimationFrame(decorateCartLines);
};
const drawer = document.querySelector('cart-drawer-component');
if (drawer) new MutationObserver(lmScheduleDecorate).observe(drawer, { childList: true, subtree: true });
document.addEventListener('cart:update', () => setTimeout(decorateCartLines, 60));
decorateCartLines();
